// Der Drohnenschwarm — Position und Ziel jeder Drohne.
//
// Wortgleiche Entsprechung von `backend/farmining/progression/drones.py`.
//
// **Der Server führt die Drohnen selbst** (`NET-001`). Er hat seinen eigenen
// Takt und kennt die Schiffsposition; er schickt jede Drohne los, wählt ihr
// Ziel, lässt sie graben und heimkehren. FG-32 bleibt unangetastet: der Client
// meldet weiterhin nichts als Ort, Laufzeitmessung und ein Signal ohne Nutzlast.
//
// Vorher galt das Gegenteil, und die Begründung war richtig: der Suchradius ging
// vom **Schiff** aus, gebohrt wurde ab der Zielzuweisung. Der Server kannte
// keine Drohnenposition, also wäre ein zweiter Radius nicht durchsetzbar gewesen
// und ein Abstandstor hätte die Anflugzeit zu einem Wert gemacht, den er nicht
// abbilden kann. Beides fällt weg, sobald er die Bewegung selbst rechnet.
//
// Solo rechnet der Client dieselbe Bewegung — zwei Fassungen derselben Regel,
// abgesichert durch dieselben Testfälle auf beiden Seiten.

import { TAU } from '../core/math';
import { balancing } from '../data/balancing';
import { pickDroneTarget, type Target } from './targeting';

/** Radius des Ruheplatzes um das Schiff. */
export const ORBIT = 52;

/** Spielraum auf die Ankunft — beide Seiten laufen mit verschiedenen Takten. */
export const ARRIVE_SLACK = 6;

/**
 * Eine Drohne. Sie hält nur die **ID** ihres Ziels, nie den Brocken.
 *
 * Eine Referenz überlebte den zerstörten Brocken und hielte ihn im Speicher;
 * die ID läuft ins Leere und wird beim nächsten Schritt neu vergeben.
 */
export interface Drone {
  x: number;
  y: number;
  targetId: string;
  /** Letzte bekannte Zielposition — Anflug und Sprite-Winkel lesen sie. */
  tx: number;
  ty: number;
  /** Nachladezeit des Bohrers. Gehört der Drohne, nicht der Bewegung. */
  cooldown: number;
}

export function makeDrone(x: number, y: number, cooldown = 0): Drone {
  return { x, y, targetId: '', tx: x, ty: y, cooldown };
}

/** Den Bestand an die gekaufte Zahl angleichen. Neue starten am Schiff. */
export function fitDrones(units: Drone[], count: number, shipX: number, shipY: number): void {
  while (units.length < count) units.push(makeDrone(shipX, shipY));
  while (units.length > count) units.pop();
}

/**
 * Jeder Drohne ein eigenes Ziel geben — Suchkreis von **ihr** aus.
 *
 * `candidates` ist **eine** Abfrage für den ganzen Schwarm; jede Drohne filtert
 * daraus ihren eigenen Kreis. Einmal je Drohne abzufragen wäre der Hot Path,
 * den der gemeinsame Zielpuffer gerade vermeidet (Gate FG-14).
 *
 * `taken` ist die **Reservierung vor der Auswahl**, kein Nachfilter: die
 * Drohnen überspringen, worauf ein anderer Kanal schon zielt, statt ihren Schuss
 * zu verlieren.
 *
 * Bestehende Ziele werden zuerst bestätigt: ohne diese Klebrigkeit wechselten
 * die Drohnen bei jedem Ruck des Schiffs den Brocken.
 */
export function assignDroneTargets<TTarget extends Target>(
  units: Drone[],
  candidates: readonly TTarget[],
  taken: Set<string>,
  shipX: number,
  shipY: number,
  radius: number,
  wish = '',
): void {
  const leash = balancing().world.droneLeash;
  for (const unit of units) {
    const target = unit.targetId ? findById(candidates, unit.targetId) : undefined;
    if (target && reachable(unit, target, shipX, shipY, radius, leash)) {
      taken.add(target.id);
      unit.tx = target.x; unit.ty = target.y;
    } else {
      unit.targetId = '';
    }
  }

  for (const unit of units) {
    if (unit.targetId) continue;
    // `wish` ist eine **Neigung**, kein Filter: ist keins in Reichweite, graben
    // sie die nächstbesten. Ein Schiff, dessen Drohnen plötzlich nichts mehr
    // tun, sieht nach einem Fehler aus, nicht nach einer Einstellung.
    const target = pickDroneTarget(candidates, taken, unit.x, unit.y, radius, wish);
    // Die Leine gilt schon bei der Auswahl: ein Ziel, das die Drohne nie
    // erreichen darf, wäre ein Anflug ins Leere.
    if (!target || !withinLeash(target.x, target.y, shipX, shipY, leash)) continue;
    taken.add(target.id);
    unit.targetId = target.id;
    unit.tx = target.x; unit.ty = target.y;
  }
}

/** Fliegen. Ohne Ziel zurück auf den Orbitplatz, immer an der Leine. */
export function flyDrones(units: Drone[], dt: number, shipX: number, shipY: number): void {
  const world = balancing().world;
  const count = Math.max(1, units.length);
  for (let index = 0; index < units.length; index++) {
    const unit = units[index];
    if (unit.targetId) {
      glide(unit, dt, unit.tx, unit.ty, world.droneHover, world.droneSpeed);
    } else {
      const angle = (index / count) * TAU;
      glide(unit, dt, shipX + Math.cos(angle) * ORBIT, shipY + Math.sin(angle) * ORBIT, 0, world.droneSpeed);
    }
    leash(unit, shipX, shipY, world.droneLeash);
  }
}

/**
 * Ist die Drohne an ihrem Ziel — also bohrt sie?
 *
 * Seit sie ausschwärmt, ist der Anflug **Wirkung**: sie gräbt vor Ort, nicht ab
 * der Zielzuweisung. Beide Seiten rechnen dieselbe gleichförmige Bewegung, also
 * dieselbe Ankunft.
 */
export function droneArrived(unit: Drone, radius: number): boolean {
  const reach = balancing().world.droneHover + radius + ARRIVE_SLACK;
  return Math.hypot(unit.tx - unit.x, unit.ty - unit.y) <= reach;
}

function findById<TTarget extends Target>(
  candidates: readonly TTarget[], id: string,
): TTarget | undefined {
  for (const target of candidates) {
    if (target.id === id) return target;
  }
  return undefined;
}

/**
 * Darf diese Drohne dieses Ziel behalten?
 *
 * Im eigenen Suchkreis **und** an der Leine. Der zweite Teil ist der Grund,
 * warum ein einmal vergebenes Ziel wieder verfallen kann: das Schiff fliegt
 * weiter, und die Drohne darf nicht hinterherhängen bleiben.
 */
function reachable(
  unit: Drone, target: Target, shipX: number, shipY: number, radius: number, leash: number,
): boolean {
  if (!withinLeash(target.x, target.y, shipX, shipY, leash)) return false;
  return Math.hypot(target.x - unit.x, target.y - unit.y) <= radius;
}

function withinLeash(x: number, y: number, shipX: number, shipY: number, leash: number): boolean {
  return Math.hypot(x - shipX, y - shipY) <= leash;
}

/**
 * Gleichförmig auf einen Punkt zu, mit `hover` Abstand davor.
 *
 * Gleichförmig und nicht exponentiell: eine konstante Geschwindigkeit ist
 * schrittweiten-unabhängig, und Client (60 Hz) und Server (5 Hz) kommen damit
 * zur selben Zeit an.
 */
function glide(unit: Drone, dt: number, x: number, y: number, hover: number, speed: number): void {
  const dx = x - unit.x;
  const dy = y - unit.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= hover || dist <= 1e-6) return;
  const step = Math.min(dist - hover, speed * dt);
  unit.x += (dx / dist) * step;
  unit.y += (dy / dist) * step;
}

/** Die Leine. Ohne sie verlässt die Drohne die zugestellte Nachbarschaft. */
function leash(unit: Drone, shipX: number, shipY: number, max: number): void {
  const dx = unit.x - shipX;
  const dy = unit.y - shipY;
  const dist = Math.hypot(dx, dy);
  if (dist <= max || dist <= 1e-6) return;
  unit.x = shipX + (dx / dist) * max;
  unit.y = shipY + (dy / dist) * max;
}
